import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Logo from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  /* token pre-check: "checking" while validating, "valid" shows the form,
   * "invalid" shows the expired-link state (never a silent dead end) */
  const [tokenState, setTokenState] = useState(token ? "checking" : "invalid");

  useEffect(() => {
    let alive = true;
    if (!token) {
      setTokenState("invalid");
      return;
    }
    api
      .get("/auth/reset-password/validate", { params: { token } })
      .then((res) => {
        if (alive) setTokenState(res.data?.valid ? "valid" : "invalid");
      })
      .catch(() => {
        if (alive) setTokenState("invalid");
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
        confirm_new_password: confirmPassword,
      });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      if (err?.response?.status === 400) {
        setTokenState("invalid");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050506] flex items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="reset-password-page">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[400px] w-[700px] bg-[#D4AF37]/[0.05] blur-[120px] rounded-full pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-8">
          <Logo />
        </div>
        <div className="card-lux p-8 sm:p-10">
          <p className="eyebrow-label mb-3 text-center">Account Recovery</p>
          <h1 className="font-heading text-2xl font-bold text-white text-center mb-8">
            Reset Password
          </h1>
          {!token || tokenState === "invalid" ? (
            <div className="text-center">
              <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="reset-password-invalid-link">
                This password reset link has expired or is no longer valid.
              </p>
              <Link to="/forgot-password" className="btn-outline-gold inline-block w-full py-3 text-sm mt-6 text-center" data-testid="reset-request-new-link">
                Request a New Reset Link
              </Link>
            </div>
          ) : tokenState === "checking" ? (
            <div className="text-center py-6" data-testid="reset-password-validating">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#D4AF37] animate-pulse-dot" />
              <p className="mt-4 text-sm text-[#94A3B8]">Verifying your reset link...</p>
            </div>
          ) : done ? (
            <div className="text-center">
              <p className="text-sm text-emerald-400 bg-emerald-400/10 border border-emerald-400/25 rounded-lg px-4 py-4" data-testid="reset-password-success">
                Your password has been reset successfully. Redirecting to login...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-lux"
                  placeholder="Min. 8 characters"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  data-testid="reset-password-new-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-lux"
                  placeholder="Repeat new password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              {error && (
                <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="reset-password-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="btn-gold w-full py-3 text-sm disabled:opacity-60"
                data-testid="reset-password-submit-button"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}
          <div className="gold-divider my-7" />
          <p className="text-sm text-[#64748B] text-center">
            <Link to="/login" className="text-[#D4AF37] hover:text-[#E5C158] font-medium" data-testid="reset-login-link">
              Back to sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
